/* eslint-disable import/no-unused-modules */
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider } from '@mui/material/styles';
import Cookie from 'js-cookie';
import { SnackbarProvider } from 'notistack';
import React, { useEffect, useState } from 'react';
import NextProgress from 'next-progress';
import dynamic from 'next/dynamic';

// SignupSessionBanner uses cookies - load dynamically but allow SSR
// Using loading: () => null instead of ssr: false to avoid Emotion cache issues
const SignupSessionBanner = dynamic(
  () => import('../components/Onboarding/SignupSessionBanner'),
  { loading: () => null }
);
// Post-load chrome (cookie banner, PWA update toast) — neither is needed for
// first paint, so keep them out of the shared _app chunk. Same SSR-enabled
// dynamic pattern as SignupSessionBanner.
const CookieConsent = dynamic(
  () => import('../components/CookieConsent/CookieConsent'),
  { loading: () => null }
);
const UpdatePwaNotice = dynamic(
  () => import('../components/General/UpdatePwaNotice'),
  { loading: () => null }
);
// Turns image-upload failures (esp. storage-quota hits) into snackbars with an
// upgrade CTA. Client-only event listener; not needed for first paint.
const ImageQuotaNotifier = dynamic(
  () => import('../components/General/ImageQuotaNotifier'),
  { loading: () => null }
);
import UserContext from '../components/General/UserContext';
import { ServiceWorkerProvider } from '../helpers/useServiceWorkerUpdate';
import { getTheme } from '../lib/theme';
import useCMDK from 'lib/cmdk';
import { createEmotionSsrAdvancedApproach } from 'tss-react/next/pagesDir';
import PlausibleProvider from 'next-plausible';
import '../styles/bootstrap.min.css';
import '../styles/style.css';
import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
config.autoAddCss = false;

// Sentry: eager on the server, but on the client it loads as an async chunk
// once the browser is idle — it is not needed for first paint.
if (typeof window === 'undefined') {
  import('../lib/sentry').then(m => m.init());
}

const {
  augmentDocumentWithEmotionCache,
  withAppEmotionCache,
} = createEmotionSsrAdvancedApproach({ key: 'css' });

export { augmentDocumentWithEmotionCache };

function MyApp({ Component, pageProps }) {
  const [theme, setTheme] = useState(undefined);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  function applyUndefinedTheme(e) {
    if (Cookie.get('use_dark_mode') !== undefined) return;
    if (e && e.matches) {
      document.querySelector('html').classList.add('tw-dark');
    } else {
      document.querySelector('html').classList.remove('tw-dark');
    }
  }

  useEffect(() => {
    const requestIdle =
      window.requestIdleCallback || (cb => setTimeout(cb, 2500));
    requestIdle(() => {
      import('../lib/sentry').then(m => m.init());
    });
  }, []);

  useEffect(() => {
    const darkModeCookie = Cookie.get('use_dark_mode');
    if (darkModeCookie !== undefined) {
      const newTheme = darkModeCookie !== 'true' ? 'light' : 'dark';
      if (darkModeCookie !== 'true')
        document.querySelector('html').classList.remove('tw-dark');
      else document.querySelector('html').classList.add('tw-dark');
      setTheme(newTheme);
    } else if (theme === undefined) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      applyUndefinedTheme(mediaQuery);
      mediaQuery.addEventListener('change', applyUndefinedTheme);
      return () =>
        mediaQuery.removeEventListener('change', applyUndefinedTheme);
    }

    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentNode) {
      jssStyles.parentNode.removeChild(jssStyles);
    }
  }, []);

  const setDarkMode = () => {
    const expires = new Date(new Date().getTime() + 3600 * 1000 * 24 * 30);
    Cookie.set('use_dark_mode', true, { expires });
    document.querySelector('html').classList.add('tw-dark');
    setTheme('dark');
  };

  const setLightMode = () => {
    const expires = new Date(new Date().getTime() + 3600 * 1000 * 24 * 30);
    Cookie.set('use_dark_mode', false, { expires });
    document.querySelector('html').classList.remove('tw-dark');
    setTheme('light');
  };

  const systemDarkMode = prefersDarkMode ? 'dark' : 'light';

  const { CMDK, setShowCMDK } = useCMDK();

  return (
    <PlausibleProvider
      domain="travelfeed.com"
      customDomain="https://analytics.travelfeed.com"
      selfHosted
      trackOutboundLinks
    >
      <UserContext.Provider
        value={{
          theme: theme !== undefined ? theme : systemDarkMode,
          setDarkMode,
          setLightMode,
          setShowCMDK,
          // React Hooks: https://reacttricks.com/sharing-global-data-in-next-with-custom-app-and-usecontext-hook/
        }}
      >
        <ServiceWorkerProvider>
          <ThemeProvider
            theme={getTheme({
              paletteType: theme !== undefined ? theme : systemDarkMode,
            })}
          >
            <CssBaseline />
            {/* Pass pageContext to the _document though the renderPage enhancer
                to render collected styles on server side. */}
            <SnackbarProvider maxSnack={3}>
              <CMDK />
              <ImageQuotaNotifier />
              <UpdatePwaNotice />
              <CookieConsent />
              <SignupSessionBanner />
              <NextProgress delay={200} options={{ showSpinner: false }} />
              <Component {...pageProps} />
            </SnackbarProvider>
          </ThemeProvider>
        </ServiceWorkerProvider>
      </UserContext.Provider>
    </PlausibleProvider>
  );
}

export default withAppEmotionCache(MyApp);
